import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  StatsBillingSchemaWithIdNullable,
  StatsCreditsSchemaNullable,
} from '@iconik-io/iconik-api/src/services/stats';

import type { AnyObject } from '../../types/common';
import type { BillingOverviewAppState } from './types';

export const initialState: BillingOverviewAppState = {
  firstBillingEntry: null,
  entries: [],
  currentBalance: 0,
  taxValue: null,
  currency: null,
  entryCurrency: '',
  billingCountry: null,
  paymentProcessing: false,
  chargeId: '',
};

/**
 * A billing fetch only reports the entry fields; the rest of the state is
 * owned by the credits config and the payment flow. The balance and currency
 * come off the first entry and may be absent, in which case the key must be
 * left alone rather than defaulted — an unknown balance reported as 0 would
 * show up as a real figure in the top-up analytics event.
 */
type BillingPayload = Partial<
  Pick<BillingOverviewAppState, 'entries' | 'currentBalance' | 'entryCurrency'>
>;

export const billingOverviewSlice = createSlice({
  name: 'billing_overview',
  initialState,
  reducers: {
    setBilling: (state, action: PayloadAction<BillingPayload>) => {
      Object.assign(state, action.payload);
    },

    setFirstBillingEntry: (
      state,
      action: PayloadAction<StatsBillingSchemaWithIdNullable | null>,
    ) => {
      state.firstBillingEntry = action.payload;
    },

    clearBilling: (state) => {
      state.entries = [];
      state.currentBalance = 0;
      state.taxValue = null;
    },

    setCreditsConfig: (
      state,
      action: PayloadAction<StatsCreditsSchemaNullable | undefined>,
    ) => {
      state.taxValue = action.payload?.vat || 0;
      state.currency = action.payload?.currency ?? null;
      state.billingCountry = action.payload?.country ?? null;
    },

    billingPaymentProcessing: (state, action: PayloadAction<boolean>) => {
      state.paymentProcessing = action.payload;
    },

    billingPaymentErrors: (
      state,
      action: PayloadAction<AnyObject[] | false | undefined>,
    ) => {
      state.paymentErrors = action.payload;
    },

    billingPaymentCharge: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.chargeId = action.payload ?? '';
    },

    billingPaymentReset: (state) => {
      state.paymentProcessing = false;
      state.paymentErrors = false;
      state.chargeId = '';
    },
  },
});

export const {
  billingPaymentCharge,
  billingPaymentErrors,
  billingPaymentProcessing,
  billingPaymentReset,
  clearBilling,
  setBilling,
  setCreditsConfig,
  setFirstBillingEntry,
} = billingOverviewSlice.actions;

export const billingOverviewReducer = billingOverviewSlice.reducer;
